import type React from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '@iconify/react';
import {
    Button,
    Input,
    Description,
    Label,
    ListBox,
    Radio,
    RadioGroup,
    Select,
    TextField,
} from '@heroui/react';
import { DatePickerField } from '@/components/datePickerField';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';
import { KIND_OPTIONS } from '../../fixedAssetsMeta';
import { AssetAccountPicker } from './AssetAccountPicker';
import type { AcquisitionKind, AssetFormModel } from './useAssetForm';

type AssetFormViewProps = {
    form: AssetFormModel;
    onCancel: () => void;
};

/** Field-level error text, shown only once the user has tried to submit. */
const useErrorText = (form: AssetFormModel) => {
    const { t } = useTranslation('pages');
    return (field: string): string | null => {
        const key = form.errors[field];
        return key && form.submitted ? t(key) : null;
    };
};

/**
 * Render-only add / edit asset form.
 *
 * The category's account mapping is shown **read-only** below the picker: the
 * mapping belongs to the class, not the asset, so it is there to confirm what the
 * acquisition entry will hit, not to be edited. The funding account appears on
 * create only — it is the credit side of the acquisition entry, not a property of
 * the asset, so editing an asset never re-asks for it.
 */
export const AssetFormView: React.FC<AssetFormViewProps> = ({
    form,
    onCancel,
}) => {
    const { t } = useTranslation('pages');
    const errorText = useErrorText(form);

    return (
        <div
            className="flex flex-col gap-5"
            {...getNodeTestIdProp('fixed-assets-form')}
        >
            {/* Bilingual name */}
            <div className="flex flex-col gap-1">
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <TextField
                        className="flex flex-col gap-1"
                        onChange={form.onNameKaChange}
                        value={form.nameKa}
                        {...getNodeTestIdProp('fixed-assets-form-name-ka')}
                    >
                        <Label>{t('fixedAssets.form.fields.nameKa')}</Label>
                        <Input
                            placeholder={t(
                                'fixedAssets.form.fields.namePlaceholder',
                            )}
                        />
                    </TextField>
                    <TextField
                        className="flex flex-col gap-1"
                        onChange={form.onNameEnChange}
                        value={form.nameEn}
                        {...getNodeTestIdProp('fixed-assets-form-name-en')}
                    >
                        <Label>{t('fixedAssets.form.fields.nameEn')}</Label>
                        <Input
                            placeholder={t(
                                'fixedAssets.form.fields.namePlaceholder',
                            )}
                        />
                    </TextField>
                </div>
                {errorText('name') ? (
                    <span
                        className="text-xs text-danger"
                        {...getNodeTestIdProp('fixed-assets-form-name-error')}
                    >
                        {errorText('name')}
                    </span>
                ) : null}
            </div>

            {/* Category + its (read-only) account mapping */}
            <div className="flex flex-col gap-2">
                <Select
                    onChange={(next) =>
                        next && form.onCategoryChange(String(next))
                    }
                    placeholder={t('fixedAssets.form.fields.category')}
                    value={form.categoryId}
                >
                    <Label>{t('fixedAssets.form.fields.category')}</Label>
                    <Select.Trigger
                        {...getNodeTestIdProp('fixed-assets-form-category')}
                    >
                        <Select.Value />
                        <Select.Indicator />
                    </Select.Trigger>
                    <Select.Popover>
                        <ListBox>
                            {form.categoryOptions.map((option) => (
                                <ListBox.Item
                                    id={option.id}
                                    key={option.id}
                                    textValue={option.label}
                                >
                                    {option.label}
                                    <ListBox.ItemIndicator />
                                </ListBox.Item>
                            ))}
                        </ListBox>
                    </Select.Popover>
                </Select>

                {form.mapping.length > 0 ? (
                    <dl
                        className="flex flex-col gap-2 rounded-xl bg-surface-secondary p-3"
                        {...getNodeTestIdProp('fixed-assets-form-mapping')}
                    >
                        <span className="text-xs text-muted">
                            {t('fixedAssets.form.mapping.note')}
                        </span>
                        {form.mapping.map((entry) => (
                            <div
                                className="flex items-baseline justify-between gap-3"
                                key={entry.labelKey}
                            >
                                <dt className="text-xs text-muted">
                                    {t(entry.labelKey)}
                                </dt>
                                <dd className="flex min-w-0 flex-col items-end">
                                    <span className="text-xs tabular-nums text-muted">
                                        {entry.code}
                                    </span>
                                    <span className="truncate text-sm text-foreground">
                                        {entry.name}
                                    </span>
                                </dd>
                            </div>
                        ))}
                    </dl>
                ) : null}
            </div>

            {/* Acquisition date */}
            <div className="flex flex-col gap-1">
                <DatePickerField
                    id="fixed-assets-form-acquisition-date"
                    label={t('fixedAssets.form.fields.acquisitionDate')}
                    name="acquisitionDate"
                    onChange={form.onAcquisitionDateChange}
                    value={form.acquisitionDate}
                    {...(errorText('acquisitionDate')
                        ? { error: errorText('acquisitionDate') as string }
                        : {})}
                />
            </div>

            {/* Cost + salvage */}
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <TextField
                    className="flex flex-col gap-1"
                    isInvalid={errorText('cost') !== null}
                    onChange={form.onCostChange}
                    value={form.cost}
                    {...getNodeTestIdProp('fixed-assets-form-cost')}
                >
                    <Label>{t('fixedAssets.form.fields.cost')}</Label>
                    <Input
                        className="text-right tabular-nums"
                        inputMode="decimal"
                        placeholder="0.00"
                    />
                    {errorText('cost') ? (
                        <span className="text-xs text-danger">
                            {errorText('cost')}
                        </span>
                    ) : null}
                </TextField>

                <TextField
                    className="flex flex-col gap-1"
                    isInvalid={errorText('salvageValue') !== null}
                    onChange={form.onSalvageChange}
                    value={form.salvageValue}
                    {...getNodeTestIdProp('fixed-assets-form-salvage')}
                >
                    <Label>{t('fixedAssets.form.fields.salvageValue')}</Label>
                    <Input
                        className="text-right tabular-nums"
                        inputMode="decimal"
                        placeholder="0.00"
                    />
                    <span className="text-xs text-muted">
                        {t('fixedAssets.form.fields.salvageHint')}
                    </span>
                    {errorText('salvageValue') ? (
                        <span className="text-xs text-danger">
                            {errorText('salvageValue')}
                        </span>
                    ) : null}
                </TextField>
            </div>

            {/* Useful life — a bounded control, not a full-width one */}
            <TextField
                className="flex w-full max-w-[13rem] flex-col gap-1"
                isInvalid={errorText('usefulLifeMonths') !== null}
                onChange={form.onUsefulLifeChange}
                value={form.usefulLifeMonths}
                {...getNodeTestIdProp('fixed-assets-form-useful-life')}
            >
                <Label>{t('fixedAssets.form.fields.usefulLife')}</Label>
                <Input
                    className="tabular-nums"
                    inputMode="numeric"
                    placeholder="60"
                />
                <span className="text-xs text-muted">
                    {t('fixedAssets.form.fields.usefulLifeHint')}
                </span>
                {errorText('usefulLifeMonths') ? (
                    <span className="text-xs text-danger">
                        {errorText('usefulLifeMonths')}
                    </span>
                ) : null}
            </TextField>

            {/*
              Buying something now vs. recording something already owned.
              Create only — an edit can change neither, because neither is a
              property of the asset: one posts an entry and the other does not.

              This is not a preference. A tenant's existing assets are already in
              the ledger through the opening entry, so creating them as
              acquisitions would post their cost a second time and break the
              tie-out on day one — invisibly, because a seeded register
              reconciles perfectly either way.
            */}
            {form.isEdit ? null : (
                <RadioGroup
                    className="gap-2"
                    onChange={(value) =>
                        form.onKindChange(value as AcquisitionKind)
                    }
                    value={form.kind}
                >
                    <Label>{t('fixedAssets.form.kind.legend')}</Label>
                    {/*
                      Two structural details, both found by driving the form:

                      1. The compound `Radio.Content > Radio.Control >
                         Radio.Indicator` is load-bearing, not ceremony. Without
                         it HeroUI renders the label with **no radio at all** —
                         the choice reads as two headings and nothing shows which
                         is selected.
                      2. The two flex directions are the **opposite** of what
                         the anatomy suggests: `.radio` is a *row* and
                         `.radio__content` is a *column*. So a `Description` left
                         as a sibling of `Radio.Content` becomes a second column
                         beside the label, and the control stacks above its own
                         label. Both label and description go *inside*
                         `Radio.Content`, and the row direction is set
                         explicitly rather than assumed.
                      3. The active themes flatten the control to
                         `border-radius: 0`, which renders a radio as a square —
                         i.e. as a checkbox, promising multi-select where only
                         one choice is possible. `rounded-full` is set back here.
                         This is the app's only `RadioGroup`, so the theme gap
                         has had nothing else to show up on; it wants fixing in
                         `index.css` rather than per-call-site.
                    */}
                    {KIND_OPTIONS.map((option) => (
                        <Radio
                            key={option.value}
                            value={option.value}
                            {...getNodeTestIdProp(
                                `fixed-assets-form-kind-${option.slot}`,
                            )}
                        >
                            <Radio.Content className="flex-row items-start gap-3">
                                <Radio.Control className="mt-0.5 shrink-0 rounded-full">
                                    <Radio.Indicator className="rounded-full before:rounded-full" />
                                </Radio.Control>
                                <span className="flex flex-col gap-0.5">
                                    <span className="text-sm text-foreground">
                                        {t(option.labelKey)}
                                    </span>
                                    <Description>
                                        {t(option.hintKey)}
                                    </Description>
                                </span>
                            </Radio.Content>
                        </Radio>
                    ))}
                </RadioGroup>
            )}

            {/* The credit side. Required on an acquisition, refused on an opening. */}
            {form.isEdit || form.isOpening ? null : (
                <div className="flex flex-col gap-1">
                    <Label id="fixed-assets-form-funding-label">
                        {t('fixedAssets.form.fields.fundingAccount')}
                    </Label>
                    <AssetAccountPicker
                        ariaLabel={t('fixedAssets.form.fields.fundingAccount')}
                        isInvalid={errorText('fundingAccountId') !== null}
                        onChange={form.onFundingAccountChange}
                        options={form.accountOptions}
                        placeholder={t(
                            'fixedAssets.form.fields.fundingPlaceholder',
                        )}
                        slot="funding"
                        value={form.fundingAccountId}
                    />
                    <span className="text-xs text-muted">
                        {t('fixedAssets.form.fields.fundingHint')}
                    </span>
                    {errorText('fundingAccountId') ? (
                        <span className="text-xs text-danger">
                            {errorText('fundingAccountId')}
                        </span>
                    ) : null}
                </div>
            )}

            {/* The opening position: what has already been depreciated. */}
            {form.isOpening ? (
                <div
                    className="flex flex-col gap-3 rounded-lg bg-surface-secondary p-3"
                    {...getNodeTestIdProp('fixed-assets-form-opening')}
                >
                    <p className="text-xs text-muted">
                        {t('fixedAssets.form.opening.note')}
                    </p>
                    <TextField
                        onChange={form.onOpeningAccumulatedChange}
                        value={form.openingAccumulated}
                    >
                        <Label>
                            {t('fixedAssets.form.opening.accumulated')}
                        </Label>
                        <Input
                            inputMode="decimal"
                            placeholder="0.00"
                            {...getNodeTestIdProp(
                                'fixed-assets-form-opening-accumulated',
                            )}
                        />
                        <span className="text-xs text-muted">
                            {t('fixedAssets.form.opening.accumulatedHint')}
                        </span>
                        {errorText('openingAccumulated') ? (
                            <span className="text-xs text-danger">
                                {errorText('openingAccumulated')}
                            </span>
                        ) : null}
                    </TextField>
                    <DatePickerField
                        id="fixed-assets-form-opening-through"
                        label={t('fixedAssets.form.opening.through')}
                        name="openingDepreciatedThrough"
                        onChange={form.onOpeningThroughDateChange}
                        value={form.openingThroughDate}
                    />
                    <span className="text-xs text-muted">
                        {t('fixedAssets.form.opening.throughHint')}
                    </span>
                </div>
            ) : null}

            {form.saveError ? (
                <p
                    className="text-sm text-danger"
                    {...getNodeTestIdProp('fixed-assets-form-error')}
                >
                    {form.saveError}
                </p>
            ) : null}

            <div className="flex items-center justify-end gap-2">
                <Button
                    onPress={onCancel}
                    variant="tertiary"
                    {...getNodeTestIdProp('fixed-assets-form-cancel')}
                >
                    {t('fixedAssets.actions.cancel')}
                </Button>
                <Button
                    isPending={form.isSaving}
                    onPress={form.onSubmit}
                    variant="primary"
                    {...getNodeTestIdProp('fixed-assets-form-save')}
                >
                    <Icon aria-hidden icon="gravity-ui:check" />
                    {form.isEdit
                        ? t('fixedAssets.actions.saveChanges')
                        : form.isOpening
                          ? // No entry to review: an opening asset posts nothing,
                            // so promising a review would promise a step that
                            // never comes.
                            t('fixedAssets.actions.createOpening')
                          : t('fixedAssets.actions.createAndReview')}
                </Button>
            </div>
        </div>
    );
};
